import { useState, useMemo } from 'react';
import { useAddWorkOrderCharge } from '@api/enterprise/workorder';
import { useApiToast } from '@hooks/useApiToast';
import type { RepairPartViewModel } from '@api/enterprise/openapi';

type SelectedPartData = {
  quantity: number;
  cost: number | null | undefined;
  partName: string | null | undefined;
  description: string | null | undefined;
};

type UsePartSelectionParams = {
  jobNumber: string;
  repairPartsData: { value?: RepairPartViewModel[] } | undefined;
  onSuccess?: () => void;
};

export const usePartSelection = ({
  jobNumber,
  repairPartsData,
  onSuccess
}: UsePartSelectionParams) => {
  const { showSuccess, showError } = useApiToast();
  const [selectedParts, setSelectedParts] = useState<
    Map<string, SelectedPartData>
  >(new Map());
  const [isAddingParts, setIsAddingParts] = useState(false);

  const { mutateAsync: addCharge } = useAddWorkOrderCharge(undefined, {
    suppressToast: true
  });

  const partsLookup = useMemo(() => {
    const map = new Map();
    repairPartsData?.value?.forEach(part => {
      if (part.companyName && part.partNumber) {
        const key = `${part.companyName}:${part.partNumber}`;
        map.set(key, part);
      }
    });
    return map;
  }, [repairPartsData]);

  const handleTogglePart = (
    companyName: string | undefined,
    partNumber: string | undefined
  ) => {
    if (!companyName || !partNumber) return;

    const partKey = `${companyName}:${partNumber}`;
    const newSelected = new Map(selectedParts);

    if (newSelected.has(partKey)) {
      newSelected.delete(partKey);
    } else {
      const partData = partsLookup.get(partKey);
      newSelected.set(partKey, {
        quantity: 1,
        cost: partData?.cost,
        partName: partData?.partName,
        description: partData?.description
      });
    }

    setSelectedParts(newSelected);
  };

  const handleQuantityChange = (partKey: string, quantity: number) => {
    const newSelected = new Map(selectedParts);
    const existing = newSelected.get(partKey);
    if (existing) {
      newSelected.set(partKey, { ...existing, quantity });
      setSelectedParts(newSelected);
    }
  };

  const isPartSelected = (
    companyName: string | undefined,
    partNumber: string | undefined
  ) => {
    if (!companyName || !partNumber) return false;
    return selectedParts.has(`${companyName}:${partNumber}`);
  };

  const getPartQuantity = (
    companyName: string | undefined,
    partNumber: string | undefined
  ) => {
    if (!companyName || !partNumber) return 1;
    return selectedParts.get(`${companyName}:${partNumber}`)?.quantity || 1;
  };

  const handleAddParts = async () => {
    const partsToAdd = Array.from(selectedParts.entries()).map(
      ([partKey, partData]) => {
        const [companyName, partNumber] = partKey.split(':');
        return { companyName, partNumber, ...partData };
      }
    );

    if (partsToAdd.length === 0) {
      onSuccess?.();
      return;
    }

    setIsAddingParts(true);

    try {
      const results: Array<{ status: 'fulfilled' | 'rejected' }> = [];

      for (const {
        companyName,
        partNumber,
        quantity,
        cost,
        partName
      } of partsToAdd) {
        try {
          await addCharge({
            jobNumber,
            chargeData: {
              chargeType: 'REPAIR PART',
              lineDescription: partName || partNumber,
              lineQuantity: quantity,
              lineCost: cost || 0,
              lineTotalCost: (cost || 0) * quantity,
              lineType: null,
              partCompanyName: companyName,
              partNumber
            } as any
          });
          results.push({ status: 'fulfilled' });
        } catch (error) {
          results.push({ status: 'rejected' });
        }
      }

      const successCount = results.filter(r => r.status === 'fulfilled').length;
      const failedCount = results.filter(r => r.status === 'rejected').length;

      if (failedCount === 0) {
        showSuccess(
          successCount === 1
            ? 'addPartModal.successSingle'
            : 'addPartModal.successMultiple',
          'parts'
        );
      } else if (successCount > 0) {
        showError(
          'addPartModal.partialSuccess',
          `${successCount} added, ${failedCount} failed`,
          'parts'
        );
      } else {
        showError('addPartModal.allFailed', undefined, 'parts');
      }

      if (successCount > 0) {
        setSelectedParts(new Map());
        setIsAddingParts(false);
        onSuccess?.();
      } else {
        setIsAddingParts(false);
      }
    } catch (error) {
      console.error('Unexpected error adding parts:', error);
      showError('addPartModal.unexpectedError', error, 'parts');
      setIsAddingParts(false);
    }
  };

  const clearSelection = () => {
    setSelectedParts(new Map());
  };

  return {
    selectedParts,
    isAddingParts,
    handleTogglePart,
    handleQuantityChange,
    isPartSelected,
    getPartQuantity,
    handleAddParts,
    clearSelection
  };
};
